import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, Row, flexRender } from '@tanstack/react-table';
import { DataTable } from './ui/data-table';
import TableSortedHeader from './ui/table-sorted-header';
import SalesPagesModal from './SalesPagesModal';
import { TableCell, TableRow } from './ui/table';
import { SalesPageStates } from '../constants/sales-pages';

export interface IRowData {
    aavaId: number;
    creationDate: string;
    publicPath: string;
    occupationId: number;
    salesPagesUsers: {
        id: number;
        email: string;
        firstName: string;
        lastName: string;
        phone: string;
        state: number;
        language: string;
    };
    serviceDescription: string;
    serviceName: string;
    servicePrice: number;
    serviceVat: number;
    state: number;
}

const SalesPagesTableRow = ({ row }: { row: Row<IRowData> }) => {
    const [open, setOpen] = React.useState(false);
    return (
        <>
            <SalesPagesModal
                salesPage={row.original}
                open={open}
                setOpen={setOpen}
            />
            <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                onClick={() => setOpen(true)}
                className="cursor-pointer"
            >
                {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                        {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                        )}
                    </TableCell>
                ))}
            </TableRow>
        </>
    );
};

interface ISalesPagesTableProps {
    salesPages: IRowData[];
}

export default function SalesPagesTable({ salesPages }: ISalesPagesTableProps) {
    const { t } = useTranslation();

    const getSalesPageStateLabel = (state: number) => {
        switch (state) {
            case SalesPageStates.Closed:
                return t('sales-page.closed').toUpperCase();
            case SalesPageStates.Active:
                return t('sales-page.active').toUpperCase();
            case SalesPageStates.WaitingForApproval:
                return t('sales-page.waiting-for-approval').toUpperCase();
            default:
                return '-';
        }
    };

    const tableColumns: ColumnDef<IRowData>[] = [
        {
            id: 'email',
            accessorKey: 'salesPagesUsers.email',
            header: ({ column }) => (
                <TableSortedHeader title={t('user.user')} column={column} />
            ),
        },
        {
            id: 'publicPath',
            accessorKey: 'publicPath',
            header: ({ column }) => (
                <TableSortedHeader
                    title={t('sales-page.public-path')}
                    column={column}
                />
            ),
        },
        {
            id: 'serviceName',
            accessorKey: 'serviceName',
            header: ({ column }) => (
                <TableSortedHeader
                    title={t('sales-page.service-name')}
                    column={column}
                />
            ),
        },
        {
            id: 'servicePrice',
            header: t('sales-page.service-price'),
            accessorFn: (row) =>
                `${(row.servicePrice / 100).toFixed(2).replace('.', ',')}€`,
            meta: {
                align: 'right',
            },
        },
        {
            id: 'state',
            header: ({ column }) => (
                <TableSortedHeader
                    title={t('sales-page.state')}
                    column={column}
                />
            ),
            accessorFn: (row) => getSalesPageStateLabel(row.state),
            sortingFn: (rowA, rowB) => rowA.original.state - rowB.original.state,
            meta: {
                align: 'right',
            },
        },
    ];

    return (
        <DataTable
            columns={tableColumns}
            data={salesPages ?? []}
            TableRowComponent={SalesPagesTableRow}
        />
    );
}
